// Create a color mapping utility that can be imported by all chart components
import { peopleDwellColor } from '../../../../../config';

/**
 * Converts hex color to HSL
 * @param {string} hex - Hex color string (e.g., '#9BE2EE')
 * @return {object} - Object with h, s, l values
 */
const hexToHSL = (hex) => {
  // Remove the # if present
  hex = hex.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
};

// Get base HSL from peopleDwellColor
const baseHSL = hexToHSL(peopleDwellColor);

/**
 * Generates a deterministic color based on a string ID using peopleDwellColor as base
 * Creates variations by adjusting lightness and saturation
 * @param {string|number} id - The unique identifier for the polygon
 * @param {number} saturation - Color saturation percentage (0-100)
 * @param {number} lightness - Color lightness percentage (0-100)
 * @return {string} - HSL color string
 */
export const getPolygonColor = (id, saturation = 70, lightness = 50) => {
    // Convert the id to a string and then to a number
    const stringId = String(id);

    // Create a simple hash from the string
    let hash = 0;
    for (let i = 0; i < stringId.length; i++) {
      hash = ((hash << 5) - hash) + stringId.charCodeAt(i);
      hash = hash & hash; // Convert to 32bit integer
    }

    // Create distinct shades by distributing polygons across the blue spectrum
    // Use modulo to create evenly-spaced variations
    const polygonIndex = Math.abs(hash % 6); // 6 distinct levels

    // First color is always the exact peopleDwellColor (#9BE2EE)
    // Others are brighter purple/blue variations, avoiding darker colors
    const lightnessLevels = [baseHSL.l, 60, 70, 80, 55, 65]; // Brighter range (55-80%)
    const saturationLevels = [baseHSL.s, 75, 65, 70, 80, 60]; // Varied saturation

    const lightnessVariation = lightnessLevels[polygonIndex];
    const saturationVariation = saturationLevels[polygonIndex];

    return `hsl(${baseHSL.h}, ${saturationVariation}%, ${lightnessVariation}%)`;
  };

  // Optional: Create a method to get a darker version for borders
  export const getPolygonBorderColor = (id) => {
    // Use the same hue but darker for borders
    const stringId = String(id);
    let hash = 0;
    for (let i = 0; i < stringId.length; i++) {
      hash = ((hash << 5) - hash) + stringId.charCodeAt(i);
      hash = hash & hash;
    }

    // Slightly darker versions for borders, but still avoiding very dark colors
    const polygonIndex = Math.abs(hash % 6);
    // First border is darker version of base color, others match the brighter theme
    const lightnessLevels = [Math.max(baseHSL.l - 15, 30), 50, 60, 70, 45, 55];
    const saturationLevels = [Math.min(baseHSL.s + 5, 95), 80, 70, 75, 85, 65];

    const lightnessVariation = lightnessLevels[polygonIndex];
    const saturationVariation = saturationLevels[polygonIndex];

    return `hsl(${baseHSL.h}, ${saturationVariation}%, ${lightnessVariation}%)`;
  };

  // Optional: Create predefined colors for specific polygons if needed
  export const POLYGON_COLOR_OVERRIDES = {
    // Add any specific polygon_id to color mappings here
    // "123": "hsl(240, 70%, 50%)"
  };